import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import { Building2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useWorkdayGate } from '../../workday/WorkdayGateContext';
import { fetchWorkingDistributor, selectWorkingDistributor } from '../../workday/api';
import { isMultiDistributorRole } from '../../workday/constants';

const C = {
    primary: '#09b8a9',
    primaryDark: '#06857a',
    text: '#383b50',
    textSec: '#6b7280',
    border: '#e5edec',
    soft: '#e6f7f5',
};

/**
 * Header chip showing the Salesman's active Working Distributor Session
 * with a controlled Switch action.
 */
export default function WorkingDistributorChip() {
    const { user } = useAuth();
    const { gate, refresh, blocked } = useWorkdayGate();
    const [session, setSession] = useState(null);
    const [eligible, setEligible] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedId, setSelectedId] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!isMultiDistributorRole(user?.role) || blocked) return;
        try {
            const data = await fetchWorkingDistributor();
            setSession(data?.session || null);
            setEligible(data?.eligible || []);
            try {
                if (data?.session?.workingDistributorId) {
                    sessionStorage.setItem(
                        'tauris.workingDistributorId',
                        String(data.session.workingDistributorId)
                    );
                } else {
                    sessionStorage.removeItem('tauris.workingDistributorId');
                }
            } catch { /* ignore */ }
        } catch {
            /* non-critical chrome */
        }
    }, [user?.role, blocked]);

    useEffect(() => {
        load();
    }, [load, gate?.workingDistributor?.session?.id]);

    if (!isMultiDistributorRole(user?.role)) return null;

    const name = session?.workingDistributorName
        || gate?.workingDistributor?.session?.workingDistributorName
        || null;
    const canSwitch = eligible.length > 1;

    if (!name && !canSwitch) return null;

    const openSwitch = () => {
        setSelectedId(session?.workingDistributorId ? String(session.workingDistributorId) : '');
        setReason('');
        setOpen(true);
    };

    const confirmSwitch = async () => {
        if (!selectedId) {
            toast.error('Select a distributor');
            return;
        }
        if (session && String(selectedId) === String(session.workingDistributorId)) {
            setOpen(false);
            return;
        }
        setSaving(true);
        try {
            const data = await selectWorkingDistributor(selectedId, reason || 'Switched from header');
            setSession(data?.session || null);
            try {
                if (data?.session?.workingDistributorId) {
                    sessionStorage.setItem(
                        'tauris.workingDistributorId',
                        String(data.session.workingDistributorId)
                    );
                }
            } catch { /* ignore */ }
            await refresh({ silent: true });
            toast.success(`Now working for ${data?.session?.workingDistributorName || 'distributor'}`);
            setOpen(false);
            // Force data refresh across modules
            window.dispatchEvent(new CustomEvent('tauris:working-distributor-changed', {
                detail: data?.session,
            }));
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not switch distributor');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    maxWidth: { xs: 160, sm: 240, md: 280 },
                    px: { xs: 1, sm: 1.25 },
                    py: 0.45,
                    borderRadius: 999,
                    bgcolor: C.soft,
                    border: `1px solid rgba(9,184,169,0.35)`,
                    mr: 0.5,
                }}
                title={name ? `Working distributor: ${name}` : 'Select working distributor'}
            >
                <Building2 size={14} color={C.primaryDark} style={{ flexShrink: 0 }} />
                <Typography
                    component="span"
                    sx={{
                        fontSize: { xs: '0.68rem', sm: '0.75rem' },
                        fontWeight: 700,
                        color: C.primaryDark,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {name || 'Select distributor'}
                </Typography>
                {canSwitch && (
                    <Button
                        size="small"
                        onClick={openSwitch}
                        sx={{
                            minWidth: 0,
                            px: 0.75,
                            py: 0,
                            ml: 0.25,
                            fontSize: '0.65rem',
                            fontWeight: 800,
                            textTransform: 'none',
                            color: C.primaryDark,
                            '&:hover': { bgcolor: 'rgba(9,184,169,0.12)' },
                        }}
                        startIcon={<RefreshCw size={12} />}
                    >
                        Switch
                    </Button>
                )}
            </Box>

            <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800, color: C.text, pb: 1 }}>
                    Switch Working Distributor
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: C.textSec, fontSize: '0.85rem', mb: 2 }}>
                        This closes your current session and starts a new audited session.
                        Existing invoices and visits stay linked to the distributor active when they were created.
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        label="Distributor"
                        value={selectedId}
                        onChange={(e) => setSelectedId(e.target.value)}
                        sx={{ mb: 2 }}
                    >
                        {eligible.map((d) => (
                            <MenuItem key={String(d.id)} value={String(d.id)}>
                                {d.name}{d.isHome ? ' (Home)' : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        label="Reason (optional)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Finished territory for Dist A"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmSwitch}
                        disabled={saving || !selectedId}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: C.primary,
                            '&:hover': { bgcolor: C.primaryDark },
                        }}
                    >
                        {saving ? <CircularProgress size={18} color="inherit" /> : 'Confirm switch'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
